import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { z } from 'zod';

import { MainLayoutPanelTunnel } from '@/components/blocks/MainLayout/main-layout-tunnel';
import {
  getSavedRoutineView,
  isRoutineChatView,
  saveRoutineView,
  type RoutineChatView,
} from '@/features/routines/routine-chat-view';
import {
  closeRoutineConfiguration,
  markRoutineConfigurationEntry,
} from '@/features/routines/routine-configuration-history';
import { RoutineRunsSidebar, Routines } from '@/features/routines/routines';
import { routinesStore } from '@/features/routines/routines.state';

export const Route = createFileRoute('/_authenticated/routines/$routineId')({
  validateSearch: z.object({
    view: z
      .enum(['canvas', 'chat', 'configure'])
      .default('canvas')
      .catch('canvas'),
    template: z.string().optional().catch(undefined),
    run: z.coerce.number().int().positive().optional().catch(undefined),
  }),

  beforeLoad({ params, location }) {
    const urlSearch = new URLSearchParams(location.searchStr);
    const urlView = urlSearch.get('view');
    const urlRun = Number(urlSearch.get('run'));
    const run = Number.isSafeInteger(urlRun) && urlRun > 0 ? urlRun : undefined;

    const hasLegacyConfigure = urlSearch.has('configure');
    const view =
      urlSearch.get('configure') === 'true' || urlView === 'configure'
        ? 'configure'
        : isRoutineChatView(urlView)
          ? urlView
          : getSavedRoutineView(params.routineId);

    if (urlView !== view || hasLegacyConfigure) {
      throw redirect({
        to: '/routines/$routineId',
        params: { routineId: params.routineId },
        search: {
          view,
          template: urlSearch.get('template') ?? undefined,
          run,
        },
        replace: true,
      });
    }
  },

  component: RoutineChatRoute,
});

function RoutineChatRoute() {
  const { routineId } = Route.useParams();
  const { view, run } = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();

  const numericRoutineId = Number(routineId);
  const contentView =
    view === 'configure' ? getSavedRoutineView(routineId) : view;

  const followedRunRef = useRef<{
    routineId: string;
    reportId: number;
  } | null>(null);

  useLayoutEffect(() => {
    const followedRun = followedRunRef.current;
    if (
      followedRun &&
      (followedRun.routineId !== routineId || followedRun.reportId !== run)
    ) {
      followedRunRef.current = null;
    }
  }, [routineId, run]);

  const requestedReportId =
    followedRunRef.current?.routineId === routineId &&
    followedRunRef.current.reportId === run
      ? undefined
      : run;

  useEffect(() => {
    if (view !== 'configure') saveRoutineView(routineId, view);
  }, [routineId, view]);

  useEffect(() => {
    if (view === 'configure') routinesStore.setShowHistory(true);
  }, [view]);

  const setView = (nextView: RoutineChatView) => {
    void navigate({
      to: '/routines/$routineId',
      params: { routineId },
      search: (prev) => ({ ...prev, view: nextView }),
    });
  };

  const setConfigurationOpen = (open: boolean) => {
    if (!open) {
      closeRoutineConfiguration(router.history, () => {
        void navigate({
          to: '/routines/$routineId',
          params: { routineId },
          search: (prev) => ({
            ...prev,
            view: getSavedRoutineView(routineId),
          }),
          replace: true,
        });
      });
      return;
    }

    routinesStore.setShowHistory(true);
    void navigate({
      to: '/routines/$routineId',
      params: { routineId },
      search: (prev) => ({ ...prev, view: 'configure' }),
      state: markRoutineConfigurationEntry,
    });
  };

  const openRoutineImprovementChat = (reportId: number) => {
    saveRoutineView(routineId, 'chat');

    void navigate({
      to: '/routines/$routineId',
      params: { routineId },
      search: (prev) => ({ ...prev, view: 'chat', run: reportId }),
      replace: true,
    });
  };

  const followRun = (reportId: number) => {
    followedRunRef.current = { routineId, reportId };
    if (run === reportId) return;

    void navigate({
      to: '/routines/$routineId',
      params: { routineId },
      search: (prev) => ({ ...prev, run: reportId }),
      replace: true,
    });
  };

  return (
    <>
      <Routines
        routineId={numericRoutineId}
        view={contentView}
        requestedReportId={requestedReportId}
        onViewChange={setView}
        onFollowingReportChange={followRun}
      />
      <MainLayoutPanelTunnel.In>
        <RoutineRunsSidebar
          routineId={numericRoutineId}
          configurationOpen={view === 'configure'}
          onConfigurationOpenChange={setConfigurationOpen}
          onImproveRoutineOpen={openRoutineImprovementChat}
        />
      </MainLayoutPanelTunnel.In>
    </>
  );
}
